import React, { useState, useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import { Box, Typography, IconButton, TextField, Button, Slide } from '@mui/material';

interface KPI {
  id: string;
  description: string;
}

interface KPIsData {
  [key: string]: KPI;
}

interface ProcessInfoSlideProps {
  open: boolean;
  onClose: () => void;
  processDescription: string;
  kpis: KPIsData;
  onUpdate: (processDescription: string, kpis: KPIsData) => void;
}

const ProcessInfoSlide: React.FC<ProcessInfoSlideProps> = ({
  open,
  onClose,
  processDescription: initialProcessDescription,
  kpis: initialKpis,
  onUpdate,
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [processDescription, setProcessDescription] = useState(initialProcessDescription);
  const [kpis, setKpis] = useState<KPIsData>(initialKpis || {});

  // Reset state when props change
  useEffect(() => {
    setProcessDescription(initialProcessDescription);
    setKpis(initialKpis || {});
  }, [initialProcessDescription, initialKpis]);

  const handleEditToggle = () => {
    if (isEditMode) {
      // Reset to initial values if canceling edit
      setProcessDescription(initialProcessDescription);
      setKpis(initialKpis || {});
    }
    setIsEditMode(!isEditMode);
  };

  const handleUpdate = () => {
    onUpdate(processDescription, kpis);
    setIsEditMode(false);
  };

  const handleKpiChange = (key: string, value: string) => {
    setKpis((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        description: value,
      },
    }));
  };

  const handleAddKpi = (afterKey: string) => {
    setKpis((prevKpis) => {
      const keys = Object.keys(prevKpis)
        .map(Number)
        .sort((a, b) => a - b);
      const afterKeyNum = Number(afterKey);
      const newKpis: KPIsData = {};

      let inserted = false;
      let newKeyCounter = 1;

      for (const key of keys) {
        // Add existing KPI with new key
        newKpis[String(newKeyCounter)] = prevKpis[String(key)];
        newKeyCounter++;

        // Insert new KPI after the specified key
        if (key === afterKeyNum && !inserted) {
          newKpis[String(newKeyCounter)] = {
            id: uuid(),
            description: '',
          };
          newKeyCounter++;
          inserted = true;
        }
      }

      return newKpis;
    });
  };

  const handleRemoveKpi = (keyToRemove: string) => {
    if (Object.keys(kpis).length <= 1) {
      return; // Prevent removing the last KPI
    }

    setKpis((prevKpis) => {
      const keys = Object.keys(prevKpis)
        .map(Number)
        .sort((a, b) => a - b);
      const removeKeyNum = Number(keyToRemove);
      const newKpis: KPIsData = {};

      let newKeyCounter = 1;

      for (const key of keys) {
        // Skip the key to be removed
        if (key === removeKeyNum) {
          continue;
        }

        // Add existing KPI with new sequential key
        newKpis[String(newKeyCounter)] = prevKpis[String(key)];
        newKeyCounter++;
      }

      return newKpis;
    });
  };
  // Get KPI entries sorted by key
  const kpiEntries = Object.keys(kpis)
    ?.map(Number)
    ?.sort((a, b) => a - b)
    ?.map((key) => [String(key), kpis[String(key)]] as [string, KPI]);

  return (
    <Slide direction="left" in={open} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          top: { xs: 0, sm: '15%' },
          right: 0,
          marginRight: { xs: 0, sm: '20px' },
          width: { xs: '100%', sm: 440 },
          maxWidth: { xs: '98%', md: 440 },
          height: { xs: '100%', sm: '75vh' },
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: { xs: 2, sm: '20px' },
          zIndex: 1299,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          borderRadius: { xs: 0, sm: 2 },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: '4px',
            position: 'sticky',
            top: 0,
            backgroundColor: 'background.paper',
            zIndex: 1,
            pb: 2,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: '18px', color: 'text.primary' }}>
            Process Information
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!isEditMode && (
              <IconButton onClick={handleEditToggle} sx={{ color: 'text.secondary' }}>
                <img src={'/images/edit-new.svg'} alt="edit" height={'16px'} width={'16px'} />
              </IconButton>
            )}
            <IconButton onClick={onClose} sx={{ color: 'text.secondary' }}>
              <img src={'/images/cross-new.svg'} alt="close" height={'16px'} width={'16px'} />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
          {/* Process Description Section */}
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 400,
                color: 'text.primary',
                fontSize: '14px',
                mb: '4px',
              }}
            >
              Process description
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={1}
              maxRows={6}
              value={processDescription}
              onChange={(e) => setProcessDescription(e.target.value)}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.default',
                  fontSize: '16px',
                  fontWeight: 400,
                },
              }}
              disabled={!isEditMode}
            />
          </Box>

          {/* KPIs Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 1,
                fontWeight: 400,
                color: 'text.primary',
                fontSize: '14px',
              }}
            >
              KPIs
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {kpiEntries.map(([key, kpi]) => (
                <Box
                  key={key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <TextField
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={6}
                    value={kpi.description}
                    onChange={(e) => handleKpiChange(key, e.target.value)}
                    variant="outlined"
                    placeholder="Enter KPI description"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'background.paper',
                        fontSize: '16px',
                        fontWeight: 400,
                      },
                    }}
                    disabled={!isEditMode}
                  />

                  {isEditMode && (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => handleAddKpi(key)}
                        sx={{
                          color: 'primary.main',
                          '&:hover': { bgcolor: 'primary.lighter' },
                          mt: 0.5,
                        }}
                      >
                        <img src={'/images/add.svg'} alt="add" height={'16px'} width={'16px'} />
                      </IconButton>
                      {kpiEntries.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveKpi(key)}
                          sx={{
                            color: 'error.main',
                            '&:hover': { bgcolor: 'error.lighter' },
                            mt: 0.5,
                          }}
                        >
                          <img
                            src={'/images/subtract.svg'}
                            alt="subtract"
                            height={'16px'}
                            width={'16px'}
                          />
                        </IconButton>
                      )}
                    </>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Footer with Update Button */}
        {isEditMode && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              pt: 2,
              position: 'sticky',
              bottom: 0,
              backgroundColor: 'background.paper',
            }}
          >
            <Button
              variant="outlined"
              onClick={handleEditToggle}
              sx={{ minWidth: 100, fontWeight: 400, fontSize: 16 }}
            >
              <Typography sx={{ fontWeight: 400, fontSize: 16, textTransform: 'none' }}>
                Cancel
              </Typography>
            </Button>
            <Button variant="contained" onClick={handleUpdate} sx={{ minWidth: 100 }}>
              <Typography sx={{ fontWeight: 400, fontSize: 16, textTransform: 'none' }}>
                Update
              </Typography>
            </Button>
          </Box>
        )}
      </Box>
    </Slide>
  );
};

export default ProcessInfoSlide;
